import * as React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { normalize, random } from '@/utils';
import useWindowDimensions from '@/hooks/use-window-dimensions';

interface Props {
  choiceRefs: React.RefObject<{
    [index: number]: HTMLDivElement;
  }>;
  choiceIndex: number;
  offsetEndpointBy?: number;
}

function Arrow({
  choiceRefs,
  choiceIndex,
  offsetEndpointBy = 0,
}: Props) {
  const windowDimensions = useWindowDimensions();

  const [path, setPath] = React.useState('');

  const svgRef = React.useRef<SVGSVGElement>(null);

  React.useEffect(() => {
    // Bail out if any elements are missing
    const choiceNode = choiceRefs.current?.[choiceIndex];
    if (!choiceNode || !svgRef.current) {
      return;
    }

    const svgBB = svgRef.current.getBoundingClientRect();
    const choiceBB = choiceNode.getBoundingClientRect();

    const ARROW_WIDTH = 10;
    const ARROW_HEIGHT = 8;
    const BUMP_BY = 20;

    const startPoint = {
      x: 0,
      y: svgBB.height / 2,
    };

    // So our SVG is floating somewhere in the viewport. So is the
    // choice. Because SVGs use an internal coordinate system, I
    // need to figure out the choice's `y` coordinate by getting
    // the choice's bounding box relative to the SVG
    const endPoint = {
      x:
        choiceBB.left -
        svgBB.left -
        ARROW_WIDTH -
        16 +
        offsetEndpointBy,
      y: choiceBB.top - svgBB.top + choiceBB.height / 2,
    };

    const startBump = normalize(
      choiceIndex,
      0,
      Object.keys(choiceRefs.current).length - 1,
      BUMP_BY * 0.1,
      BUMP_BY * 0.1 * -1
    );

    const middleBump = normalize(
      choiceIndex,
      0,
      Object.keys(choiceRefs.current).length - 1,
      BUMP_BY,
      BUMP_BY * -1
    );

    // To keep things fun, pick a dynamic "midpoint" to use, so that
    // the arrow becomes more/less curvy each time
    const midpoint = random(svgBB.width * 0.25, svgBB.width * 0.6);

    const path = `
      M ${startPoint.x} ${startPoint.y + startBump}
      C ${startPoint.x + midpoint} ${startPoint.y + middleBump}
        ${endPoint.x - midpoint} ${endPoint.y}
        ${endPoint.x} ${endPoint.y}
      L ${endPoint.x - 1} ${endPoint.y + ARROW_HEIGHT}
      L ${endPoint.x + ARROW_WIDTH} ${endPoint.y}
      L ${endPoint.x - 1} ${endPoint.y - ARROW_HEIGHT}
      L ${endPoint.x} ${endPoint.y}
    `;

    setPath(path);
  }, [
    choiceIndex,
    choiceRefs,
    offsetEndpointBy,
    windowDimensions.width,
  ]);

  return (
    <Svg
      xmlns="http://www.w3.org/2000/svg"
      ref={svgRef}
      width="100%"
      height="100%"
      fill="none"
    >
      {path && <motion.path animate={{ d: path }} />}
    </Svg>
  );
}

const Svg = styled.svg`
  display: block;
  overflow: visible;

  path {
    stroke: var(--color-secondary);
    stroke-width: 3px;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export default Arrow;
